import React from 'react';

// VectorShift Resume JSON Format - matches exact output from VectorShift pipeline
export interface Template1ResumeData {
  Name: string;
  Phone: string;
  Email: string;
  City: string;
  State: string;
  LinkedIn?: string;
  Summary: string;
  
  // VectorShift uses SkillRows (4 columns per row) instead of flat Skills array
  SkillRows?: Array<{
    Skill1?: string | null;
    Skill2?: string | null;
    Skill3?: string | null;
    Skill4?: string | null;
  }>;
  
  // Legacy format - kept for backward compatibility
  Skills?: Array<{ Skill: string }>;
  
  CareerHighlights?: Array<{ chtitle: string; chdescription: string }>;
  
  ProfessionalExperience: Array<{
    job_title: string;
    company: string;
    location: string;
    start_date: string | null;
    end_date: string | null;
    Descriptions: Array<{ Description: string }>;
  }>;
  
  Education: Array<{
    College_Degree_Name: string;
    Major: string | null;
    College_Name: string;
    City: string | null;
    ST: string | null;
    Year_Earned?: string | null;
    YearEarned?: string | null; // Handle inconsistent casing from VectorShift
  }>;
  
  CertificationsAndLicenses?: Array<{
    Certification_or_License_Name: string | null;
    Institution_Organization: string | null;
    Year: string | null;
  }>;
  
  // Additional VectorShift fields
  Languages?: Array<{ Language: string | null; Proficiency_Level: string | null }>;
  AwardsAndHonors?: Array<{ Award_Name: string | null; Institution_Organization: string | null; Year: string | null }>;
  ProfessionalAffiliations?: Array<{ Membership_Role: string | null; Organization: string | null; Year: string | null }>;
  Publications?: Array<{ Title_of_Publication: string | null; Publication_Name: string | null; Date: string | null }>;
  Projects?: Array<{ Project_Name: string | null; Role: string | null; Year: string | null; Project_Description: string | null }>;
  Presentations?: Array<{ Title_of_Presentation: string | null; Event_Name: string | null; Date: string | null; Brief_Description: string | null }>;
  Portfolio?: Array<{ Portfolio: string | null }>;
  TrainingAndDevelopment?: Array<{ Course_Training_Name: string | null; Institution_Provider: string | null; Year: string | null }>;
  MilitaryExperience?: Array<{ Rank_Position: string | null; Branch: string | null; Service_Dates: string | null; Description_of_duties_or_achievements: string | null }>;
  VolunteerMemberships?: Array<{ PositionName: string | null; InstitutionOrganization: string | null; Year: string | null }>;
  
  Rules?: string;
}

// Helper: Filter arrays to remove entries where ALL values are null/empty
export function filterNullEntries<T extends Record<string, any>>(arr: T[] | undefined): T[] {
  if (!arr) return [];
  return arr.filter(item => 
    Object.values(item).some(val => val !== null && val !== undefined && val !== '')
  );
}

// Helper: Flatten SkillRows into single array of skills (handles both formats)
export function flattenSkills(data: Template1ResumeData): string[] {
  // If SkillRows exists (VectorShift format), flatten it
  if (data.SkillRows && data.SkillRows.length > 0) {
    return data.SkillRows.flatMap(row => 
      [row.Skill1, row.Skill2, row.Skill3, row.Skill4].filter((s): s is string => Boolean(s))
    );
  }
  // Fallback to legacy Skills format
  if (data.Skills && data.Skills.length > 0) {
    return data.Skills.map(s => s.Skill);
  }
  return [];
}

// Old format (for backward compatibility)
interface LegacyResumeData {
  personalInfo: {
    name: string;
    title: string;
    email: string;
    phone: string;
    location: string;
    linkedin?: string;
    website?: string;
  };
  summary: string;
  experience: Array<{
    title: string;
    company: string;
    location: string;
    dates: string;
    bullets: string[];
  }>;
  education: Array<{
    degree: string;
    school: string;
    location: string;
    dates: string;
    gpa?: string;
  }>;
  skills: string[];
}

interface Template1Props {
  data: Template1ResumeData | LegacyResumeData;
}

// Type guard to check if data is new format
function isNewFormat(data: Template1ResumeData | LegacyResumeData): data is Template1ResumeData {
  return 'Name' in data;
}

export const Template1: React.FC<Template1Props> = ({ data }) => {
  // If old format, render old template
  if (!isNewFormat(data)) {
    return (
      <div className="max-w-4xl mx-auto bg-white text-gray-900 font-sans leading-relaxed">
        {/* Header */}
        <header className="border-b-2 border-blue-600 pb-6 mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">{data.personalInfo.name}</h1>
          <h2 className="text-xl text-blue-600 font-medium mb-4">{data.personalInfo.title}</h2>
          
          <div className="flex flex-wrap gap-4 text-sm text-gray-600">
            <span>{data.personalInfo.email}</span>
            <span>•</span>
            <span>{data.personalInfo.phone}</span>
            <span>•</span>
            <span>{data.personalInfo.location}</span>
            {data.personalInfo.linkedin && (
              <>
                <span>•</span>
                <span>{data.personalInfo.linkedin}</span>
              </>
            )}
            {data.personalInfo.website && (
              <>
                <span>•</span>
                <span>{data.personalInfo.website}</span>
              </>
            )}
          </div>
        </header>

        {/* Professional Summary */}
        <section className="mb-8">
          <h3 className="text-lg font-bold text-gray-900 mb-3 uppercase tracking-wide border-b border-gray-300 pb-1">
            Professional Summary
          </h3>
          <p className="text-gray-700 leading-relaxed">{data.summary}</p>
        </section>

        {/* Professional Experience */}
        <section className="mb-8">
          <h3 className="text-lg font-bold text-gray-900 mb-4 uppercase tracking-wide border-b border-gray-300 pb-1">
            Professional Experience
          </h3>
          
          {data.experience.map((job, index) => (
            <div key={index} className="mb-6">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h4 className="font-bold text-gray-900">{job.title}</h4>
                  <p className="text-blue-600 font-medium">{job.company}</p>
                </div>
                <div className="text-right text-sm text-gray-600">
                  <p>{job.location}</p>
                  <p>{job.dates}</p>
                </div>
              </div>
              
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                {job.bullets.map((bullet, bulletIndex) => (
                  <li key={bulletIndex}>{bullet}</li>
                ))}
              </ul>
            </div>
          ))}
        </section>

        {/* Education */}
        <section className="mb-8">
          <h3 className="text-lg font-bold text-gray-900 mb-4 uppercase tracking-wide border-b border-gray-300 pb-1">
            Education
          </h3>
          
          {data.education.map((edu, index) => (
            <div key={index} className="mb-4">
              <div className="font-bold text-gray-900">{edu.degree}</div>
              <div className="text-gray-700">
                {edu.school} – {edu.location} – {edu.dates}
              </div>
            </div>
          ))}
        </section>

        {/* Skills */}
        <section>
          <h3 className="text-lg font-bold text-gray-900 mb-4 uppercase tracking-wide border-b border-gray-300 pb-1">
            Core Competencies
          </h3>
          
          <div className="grid grid-cols-3 gap-4">
            {data.skills.map((skill, index) => (
              <span key={index} className="text-gray-700 text-sm">
                {skill}
              </span>
            ))}
          </div>
        </section>
      </div>
    );
  }

  // New format - tight, compact layout
  return (
    <div className="mx-auto bg-white text-gray-900 font-serif text-[10pt] leading-tight p-6 max-w-[8.5in]">
      {/* Header */}
      <header className="text-center border-b border-gray-400 pb-2 mb-3">
        <h1 className="text-2xl font-bold text-gray-900 mb-1 tracking-wide">{data.Name.toUpperCase()}</h1>
        <div className="text-xs text-gray-600 leading-tight">
          {data.Phone} • {data.City}, {data.State} • {data.Email}
          {data.LinkedIn && ` • ${data.LinkedIn}`}
        </div>
      </header>

      {/* Professional Summary */}
      <section className="mb-3">
        <h3 className="text-center text-sm font-bold text-gray-900 mb-2 tracking-wide">
          Summary
        </h3>
        <p className="text-gray-800 text-xs leading-snug text-justify">{data.Summary}</p>
      </section>

      {/* Career Highlights */}
      {data.CareerHighlights && data.CareerHighlights.length > 0 && (
        <section className="mb-3">
          <h3 className="text-center text-sm font-bold text-gray-900 mb-2 border-t border-b border-gray-400 py-1 tracking-wide">
            Career Highlights
          </h3>
          <div className="space-y-1">
            {data.CareerHighlights.map((highlight, index) => (
              <div key={index} className="text-xs">
                <span className="font-semibold">{highlight.chtitle}:</span>{' '}
                <span className="text-gray-800">{highlight.chdescription}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Professional Experience */}
      <section className="mb-3">
        <h3 className="text-center text-sm font-bold text-gray-900 mb-2 border-t border-b border-gray-400 py-1 tracking-wide">
          Experience
        </h3>
        
        {data.ProfessionalExperience.map((job, index) => (
          <div key={index} className="mb-2">
            <div className="flex justify-between items-baseline">
              <div>
                <span className="font-bold text-xs">{job.company}</span>
                {job.location && <span className="text-xs text-gray-700 ml-2">{job.location}</span>}
              </div>
              <div className="text-xs text-gray-700">
                {job.start_date} - {job.end_date}
              </div>
            </div>
            <div className="text-xs italic text-gray-800 mb-1">{job.job_title}</div>
            
            <ul className="list-disc list-inside text-xs text-gray-800 space-y-0.5 ml-2">
              {job.Descriptions.map((desc, descIndex) => (
                <li key={descIndex} className="leading-snug">{desc.Description}</li>
              ))}
            </ul>
          </div>
        ))}
      </section>

      {/* Education */}
      <section className="mb-3">
        <h3 className="text-center text-sm font-bold text-gray-900 mb-2 border-t border-b border-gray-400 py-1 tracking-wide">
          Education
        </h3>
        
        {data.Education.map((edu, index) => (
          <div key={index} className="mb-1 text-xs">
            <div className="font-semibold text-gray-900">{edu.College_Name}</div>
            <div className="text-gray-800">
              {edu.College_Degree_Name}{edu.Major && `, ${edu.Major}`}
              {(edu.Year_Earned || edu.YearEarned) && ` – ${edu.Year_Earned || edu.YearEarned}`}
            </div>
          </div>
        ))}
      </section>

      {/* Certifications */}
      {filterNullEntries(data.CertificationsAndLicenses).length > 0 && (
        <section className="mb-3">
          <h3 className="text-center text-sm font-bold text-gray-900 mb-2 border-t border-b border-gray-400 py-1 tracking-wide">
            Certifications
          </h3>
          {filterNullEntries(data.CertificationsAndLicenses).map((cert, index) => (
            <div key={index} className="text-xs text-gray-800 mb-1">
              <span className="font-semibold">{cert.Certification_or_License_Name}</span>
              {cert.Institution_Organization && ` – ${cert.Institution_Organization}`}
              {cert.Year && ` – ${cert.Year}`}
            </div>
          ))}
        </section>
      )}

      {/* Awards */}
      {filterNullEntries(data.AwardsAndHonors).length > 0 && (
        <section className="mb-3">
          <h3 className="text-center text-sm font-bold text-gray-900 mb-2 border-t border-b border-gray-400 py-1 tracking-wide">
            Achievements
          </h3>
          {filterNullEntries(data.AwardsAndHonors).map((award, index) => (
            <div key={index} className="text-xs text-gray-800 mb-1">
              <span className="font-semibold">{award.Award_Name}</span>
              {award.Institution_Organization && ` – ${award.Institution_Organization}`}
              {award.Year && ` – ${award.Year}`}
            </div>
          ))}
        </section>
      )}

      {/* Skills */}
      {flattenSkills(data).length > 0 && (
        <section className="mb-3">
          <h3 className="text-center text-sm font-bold text-gray-900 mb-2 border-t border-b border-gray-400 py-1 tracking-wide">
            Skills
          </h3>
          <p className="text-xs text-gray-800 leading-snug">
            {flattenSkills(data).join(' • ')}
          </p>
        </section>
      )}

      {/* Volunteer */}
      {filterNullEntries(data.VolunteerMemberships).length > 0 && (
        <section>
          <h3 className="text-center text-sm font-bold text-gray-900 mb-2 border-t border-b border-gray-400 py-1 tracking-wide">
            Volunteer Leadership
          </h3>
          {filterNullEntries(data.VolunteerMemberships).map((vol, index) => (
            <div key={index} className="text-xs text-gray-800 mb-1">
              <span className="font-semibold">{vol.PositionName}</span>
              {vol.InstitutionOrganization && ` – ${vol.InstitutionOrganization}`}
              {vol.Year && ` – ${vol.Year}`}
            </div>
          ))}
        </section>
      )}
    </div>
  );
};
